import { normalizeName } from "./normalize";
import { levenshtein } from "./levenshtein";

export interface FuzzyMatchResult {
  matched: boolean;
  // true when we accepted via Levenshtein rather than an exact normalized
  // match — the caller can surface "(matched: Kyrgyzstan)" hint to teach
  // the canonical spelling.
  fuzzy: boolean;
  // the normalized candidate that won (for debugging / surfacing).
  matchedAgainst: string | null;
}

export interface FuzzyMatchInput {
  input: string;
  // All acceptable spellings for the target country: canonical name plus any
  // names array entries plus any curated aliases. The matcher normalizes
  // each candidate internally so the caller can pass raw strings.
  candidates: string[];
  tolerance: number;
}

export const fuzzyMatch = ({
  input,
  candidates,
  tolerance,
}: FuzzyMatchInput): FuzzyMatchResult => {
  const normalizedInput = normalizeName(input);
  if (normalizedInput.length === 0) {
    return { matched: false, fuzzy: false, matchedAgainst: null };
  }

  const normalizedCandidates = candidates
    .map(normalizeName)
    .filter((c) => c.length > 0);

  // Exact (post-normalization) match first.
  for (const candidate of normalizedCandidates) {
    if (candidate === normalizedInput) {
      return { matched: true, fuzzy: false, matchedAgainst: candidate };
    }
  }

  // Levenshtein fallback. We bail early on candidates whose length differs
  // from the input by more than the tolerance — they can't possibly match.
  let bestDistance = tolerance + 1;
  let bestCandidate: string | null = null;
  for (const candidate of normalizedCandidates) {
    if (Math.abs(candidate.length - normalizedInput.length) > tolerance) {
      continue;
    }
    const d = levenshtein(normalizedInput, candidate);
    if (d < bestDistance) {
      bestDistance = d;
      bestCandidate = candidate;
    }
  }
  if (bestCandidate !== null && bestDistance <= tolerance) {
    return { matched: true, fuzzy: true, matchedAgainst: bestCandidate };
  }
  return { matched: false, fuzzy: false, matchedAgainst: null };
};

export interface ClosestCountryEntry {
  iso: string;
  candidates: string[];
}

// Returns the ISO of the country whose accepted names are closest (lowest
// Levenshtein distance) to the input. Used to figure out what country the
// user *intended* when they typed a wrong answer — so we can fly to it and
// show them where it actually is. Returns null if nothing is within
// tolerance.
export const findClosestCountry = (
  input: string,
  countries: ClosestCountryEntry[],
  tolerance: number,
): { iso: string; distance: number } | null => {
  const normalizedInput = normalizeName(input);
  if (normalizedInput.length === 0) return null;
  let bestIso: string | null = null;
  let bestDistance = tolerance + 1;
  for (const country of countries) {
    for (const candidate of country.candidates) {
      const normalized = normalizeName(candidate);
      if (normalized.length === 0) continue;
      if (Math.abs(normalized.length - normalizedInput.length) > bestDistance) {
        continue;
      }
      const d = levenshtein(normalizedInput, normalized);
      if (d < bestDistance) {
        bestDistance = d;
        bestIso = country.iso;
        if (d === 0) return { iso: bestIso, distance: 0 };
      }
    }
  }
  if (bestIso !== null && bestDistance <= tolerance) {
    return { iso: bestIso, distance: bestDistance };
  }
  return null;
};
